import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from '../utils/rtk-headers';
import { getEnvironment } from '../env/env';
import {
  refetchQuerysWithAccountParam,
  refetchQuerysWithProjectTypeParam,
} from 'src/constants/refetch-account-params';
import { toProductTypeBack } from 'src/constants/products';
import type { RootState } from 'src/store/store';
import {
  AppBaseQueryError,
  getStoredUser,
  renewSession,
  withFriendlyServerError,
} from 'src/services/baseQueryAuth';

interface BaseQueryExtraOptions {
  responseHandler?: FetchArgs['responseHandler'];
  mode?: RequestMode;
  maxRetries?: number;
}

let isRefreshing = false;

const getRefreshToken = () => {
  const user = getStoredUser();

  return user.refresh || user.refresh_token;
};

const baseQueryWithAuth: BaseQueryFn<
  FetchArgs,
  unknown,
  AppBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const environment = await getEnvironment();
  const baseUrl = environment.APP_BASE_URL;

  const {
    companySlices: { managedAccountId },
    projectsSlices: { projectType },
  } = api.getState() as RootState;

  const searchParams = new URLSearchParams(location.search);
  const searchProjectType = searchParams.get('type');

  const projectTypeVar = searchProjectType || projectType;

  const urlPath = `/api/${args.url}`;
  const url = args.url.startsWith('https://') ? new URL(args.url) : new URL(urlPath, baseUrl);

  if (
    !args?.params?.account &&
    managedAccountId &&
    refetchQuerysWithAccountParam.includes(api.endpoint)
  ) {
    url.searchParams.set('account', managedAccountId);
  }

  if (projectTypeVar && refetchQuerysWithProjectTypeParam.includes(api.endpoint)) {
    url.searchParams.set('project_type', toProductTypeBack(projectTypeVar));
  }

  const updatedArgs = { ...args, url: url.toString() };
  const result = await fetchBaseQuery({
    baseUrl,
    prepareHeaders,
    ...(extraOptions?.responseHandler && { responseHandler: extraOptions.responseHandler }),
    ...(extraOptions?.mode && { mode: extraOptions.mode }),
  })(updatedArgs, api, extraOptions);

  if (result.error?.status === 400) {
    retry.fail(result.error);
  }

  if (result.error?.status === 401 && !isRefreshing) {
    isRefreshing = true;
    let isRenewed = false;

    try {
      isRenewed = await renewSession(getRefreshToken());
    } catch (error) {
      console.log('staggeredBaseQueryWithBailOut error', error);
    } finally {
      isRefreshing = false;
    }

    // A renewed session falls through so retry() repeats the request with the new token.
    if (!isRenewed) {
      retry.fail(result.error);
    }
  }

  return withFriendlyServerError(result);
};

// Annotated so endpoints see our extra options; retry()'s own RetryOptions union otherwise
// makes `extraOptions` a required property on every endpoint definition.
const staggeredBaseQueryWithBailOut: BaseQueryFn<
  FetchArgs,
  unknown,
  AppBaseQueryError,
  BaseQueryExtraOptions
> = retry(baseQueryWithAuth, {
  maxRetries: 2,
});

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: staggeredBaseQueryWithBailOut,
  tagTypes: [
    'User',
    'UsersAll',
    'Projects',
    'UserMe',
    'Accounts',
    'ProjectById',
    'ProjectTeamMembers',
    'CreateUser',
    'PaymentMethods',
    'Payments',
    'ProjectPlans',
    'ProjectIntegration',
    'ProjectFiles',
    'ProjectChats',
    'SubscriptionPermission',
    'CoCustomerAnalyticsAggregated',
    'CoCustomerAnalyticsTimeSeries',
    'CoCustomerAnalyticsChatsCount',
    'PlaygroundChat',
    'KnowledgeBase',
    'KnowledgeBaseById',
    'KnowledgeBaseFiles',
    'KnowledgeBaseFileById',
    'KnowledgeBaseFileContent',
    'KnowledgeBaseCollectors',
    'defaultTemplateFields',
    'customTemplates',
    'ConfigurationSettings',
    'ConfigurationSettingsPrompt',
  ],
  endpoints: () => ({}),
});
